import { useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export type MenuLeaf = { id: string; text: string; disabled?: boolean };
export type MenuNode = MenuLeaf | { id: string; text: string; children: MenuLeaf[] };

type Props = {
  trigger: React.ReactNode;
  items: MenuNode[];
  onSelect: (id: string) => void;
  triggerClassName?: string;
};

function VersionRowMenu({ trigger, items, onSelect, triggerClassName }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [submenuItems, setSubmenuItems] = useState<MenuLeaf[]>([]);
  const submenuParentRef = useRef<HTMLLIElement | null>(null);

  const closeAll = () => {
    setAnchorEl(null);
    setSubmenuAnchor(null);
  };

  const handleLeafClick = (id: string) => {
    onSelect(id);
    closeAll();
  };

  const handleParentEnter = (e: React.MouseEvent<HTMLLIElement>, children: MenuLeaf[]) => {
    submenuParentRef.current = e.currentTarget;
    setSubmenuItems(children);
    setSubmenuAnchor(e.currentTarget);
  };

  const handleSubmenuLeave = () => {
    setSubmenuAnchor(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={`flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 ${triggerClassName ?? ""}`}
      >
        {trigger}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeAll}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {items.map((item) => {
          if ("children" in item) {
            return (
              <MenuItem
                key={item.id}
                onMouseEnter={(e) => handleParentEnter(e, item.children)}
                sx={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}
              >
                <span>{item.text}</span>
                <KeyboardArrowRightIcon fontSize="small" />
              </MenuItem>
            );
          }
          return (
            <MenuItem
              key={item.id}
              disabled={item.disabled}
              onMouseEnter={() => setSubmenuAnchor(null)}
              onClick={() => handleLeafClick(item.id)}
            >
              {item.text}
            </MenuItem>
          );
        })}
      </Menu>
      <Menu
        anchorEl={submenuAnchor}
        open={!!submenuAnchor}
        onClose={handleSubmenuLeave}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            onMouseLeave: handleSubmenuLeave,
          },
          root: {
            sx: { pointerEvents: "none" },
          },
        }}
        sx={{ "& .MuiPaper-root": { pointerEvents: "auto" } }}
      >
        {submenuItems.map((leaf) => (
          <MenuItem key={leaf.id} disabled={leaf.disabled} onClick={() => handleLeafClick(leaf.id)}>
            {leaf.text}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default VersionRowMenu;
